const E164_PHONE_NUMBER_REGEX = /^\+[1-9]\d{1,14}$/;
const US_COUNTRY_CODE = '1';
const PHONE_CHANGE_COOLDOWN_DAYS = 7;
const PHONE_CHANGE_COOLDOWN_MS = PHONE_CHANGE_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

const BLOCKED_TEST_NUMBERS = new Set([
  '0000000000',
  '1111111111',
  '1234567890',
  '9999999999',
  '10000000000',
  '11111111111',
  '12345678900',
  '5550000000',
  '1231231234',
  '0123456789',
]);

const stripPhoneFormatting = value => value.replace(/[^\d+]/g, '');

const stripToDigits = value => (typeof value === 'string' ? value.replace(/\D/g, '') : '');

const normalizeCountryCode = countryCode => {
  if (!countryCode) {
    return US_COUNTRY_CODE;
  }

  const digits = String(countryCode).replace(/\D/g, '');
  return digits || US_COUNTRY_CODE;
};

export const normalizePhoneNumberToE164 = value => {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return trimmedValue;
  }

  const compactValue = stripPhoneFormatting(trimmedValue);

  if (E164_PHONE_NUMBER_REGEX.test(compactValue)) {
    return compactValue;
  }

  if (/^00\d+$/.test(compactValue)) {
    return `+${compactValue.slice(2)}`;
  }

  if (/^\+\d+$/.test(compactValue)) {
    return compactValue;
  }

  return trimmedValue;
};

export const normalizePhoneNumber = (phoneNumber, countryCode = US_COUNTRY_CODE) => {
  if (typeof phoneNumber !== 'string') {
    return '';
  }

  const normalizedToE164 = normalizePhoneNumberToE164(phoneNumber);
  if (E164_PHONE_NUMBER_REGEX.test(normalizedToE164)) {
    return normalizedToE164;
  }

  const digits = stripToDigits(phoneNumber);
  if (!digits) {
    return '';
  }

  const normalizedCountryCode = normalizeCountryCode(countryCode);
  const digitsWithCountry =
    normalizedCountryCode === US_COUNTRY_CODE && digits.length === 11 && digits.startsWith('1')
      ? digits
      : digits.startsWith(normalizedCountryCode)
      ? digits
      : `${normalizedCountryCode}${digits}`;
  const e164 = `+${digitsWithCountry}`;

  return E164_PHONE_NUMBER_REGEX.test(e164) ? e164 : '';
};

export const validatePhoneNumber = (phoneNumber, countryCode = US_COUNTRY_CODE) => {
  const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber, countryCode);

  if (!normalizedPhoneNumber) {
    return {
      valid: false,
      code: 'INVALID_PHONE_NUMBER',
      message: 'Please enter a valid phone number.',
    };
  }

  return {
    valid: true,
    phoneNumber: normalizedPhoneNumber,
  };
};

export const isBlockedTestPhoneNumber = phoneNumber => {
  const digits = stripToDigits(phoneNumber);
  const nationalDigits =
    digits.length > 10 && digits.startsWith(US_COUNTRY_CODE) ? digits.slice(1) : digits;
  const uniqueDigitCount = new Set(nationalDigits.split('')).size;

  return (
    BLOCKED_TEST_NUMBERS.has(digits) ||
    BLOCKED_TEST_NUMBERS.has(nationalDigits) ||
    /^(\d)\1{7,}$/.test(nationalDigits) ||
    uniqueDigitCount < 3
  );
};

export const canChangePhoneNumber = (lastChangedAt, now = new Date()) => {
  if (!lastChangedAt) {
    return true;
  }

  const lastChangedAtMs = Date.parse(lastChangedAt);
  const nowMs = now instanceof Date ? now.getTime() : Date.parse(now);

  if (!Number.isFinite(lastChangedAtMs) || !Number.isFinite(nowMs)) {
    return true;
  }

  return nowMs - lastChangedAtMs >= PHONE_CHANGE_COOLDOWN_MS;
};

export const getPhoneChangeAvailableAt = lastChangedAt => {
  const lastChangedAtMs = Date.parse(lastChangedAt || '');
  if (!Number.isFinite(lastChangedAtMs)) {
    return null;
  }

  return new Date(lastChangedAtMs + PHONE_CHANGE_COOLDOWN_MS).toISOString();
};

export const maskPhoneNumber = phoneNumber => {
  const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
  const value = normalizedPhoneNumber || (typeof phoneNumber === 'string' ? phoneNumber : '');
  const digits = stripToDigits(value);

  if (!digits) {
    return '';
  }

  const lastFour = digits.slice(-4);
  const countryCode = digits.length > 10 ? `+${digits.slice(0, digits.length - 10)}` : '+1';
  return `${countryCode} ••• ••• ${lastFour}`;
};

export const getUserPhoneData = user => {
  const protectedData = user?.attributes?.profile?.protectedData || {};
  const phoneNumber = protectedData?.phoneNumber || null;
  const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber || '');
  const verifiedPhoneNumber = normalizePhoneNumber(protectedData?.phoneNumberVerifiedValue || '');
  const phoneNumberVerified = Boolean(
    protectedData?.phoneNumberVerified === true &&
      normalizedPhoneNumber &&
      verifiedPhoneNumber === normalizedPhoneNumber
  );

  return {
    phoneNumber,
    maskedPhoneNumber: phoneNumber ? maskPhoneNumber(phoneNumber) : null,
    phoneNumberVerified,
    phoneNumberVerifiedValue: verifiedPhoneNumber || null,
    phoneNumberVerifiedAt: protectedData?.phoneNumberVerifiedAt || null,
    phoneNumberVerificationMethod: protectedData?.phoneNumberVerificationMethod || null,
    phoneNumberLastChangedAt: protectedData?.phoneNumberLastChangedAt || null,
    phoneNotificationOptOut: Boolean(protectedData?.phoneNotificationOptOut),
  };
};

export const canUserSetPhoneNumber = ({ user, phoneNumber, now = new Date() }) => {
  const protectedData = user?.attributes?.profile?.protectedData || {};
  const phoneData = getUserPhoneData(user);
  const nextPhoneNumber = normalizePhoneNumber(phoneNumber);
  const currentVerifiedPhoneNumber = normalizePhoneNumber(
    protectedData?.phoneNumberVerifiedValue ||
      (protectedData?.phoneNumberVerified === true ? phoneData.phoneNumber : '')
  );
  const isChangingVerifiedNumber =
    nextPhoneNumber && currentVerifiedPhoneNumber && currentVerifiedPhoneNumber !== nextPhoneNumber;

  if (!isChangingVerifiedNumber) {
    return { allowed: true, availableAt: null };
  }

  const allowed = canChangePhoneNumber(phoneData.phoneNumberLastChangedAt, now);

  return {
    allowed,
    availableAt: allowed ? null : getPhoneChangeAvailableAt(phoneData.phoneNumberLastChangedAt),
  };
};

export const isPhoneVerifiedForUser = (user, phoneNumber) => {
  const data = user?.attributes?.profile?.protectedData || {};
  const normalizedPhoneNumber = normalizePhoneNumber(
    phoneNumber === undefined ? data.phoneNumber : phoneNumber
  );

  return Boolean(
    data.phoneNumberVerified === true &&
      normalizedPhoneNumber &&
      normalizePhoneNumber(data.phoneNumberVerifiedValue) === normalizedPhoneNumber
  );
};

// Opted in to order texts for this exact number without a code (checkbox at checkout).
export const hasSmsConsentForPhone = (user, phoneNumber) => {
  const data = user?.attributes?.profile?.protectedData || {};
  const consentPhoneNumber = normalizePhoneNumber(data.smsConsent?.phoneNumber || '');
  return Boolean(
    consentPhoneNumber && consentPhoneNumber === normalizePhoneNumber(phoneNumber || '')
  );
};

// Mirrors the server's order-text eligibility for one number.
export const receivesOrderTextsAt = (user, phoneNumber) => {
  const profile = user?.attributes?.profile || {};
  const ordersSmsSetting = profile.privateData?.notificationPreferences?.ordersSms;
  // Same default as the server: a verified phone means order texts are on unless switched off
  const ordersSms =
    ordersSmsSetting === undefined
      ? isPhoneVerifiedForUser(user, phoneNumber)
      : ordersSmsSetting === true;
  const optedOut = Boolean(profile.protectedData?.phoneNotificationOptOut);
  return Boolean(
    ordersSms &&
      !optedOut &&
      (isPhoneVerifiedForUser(user, phoneNumber) || hasSmsConsentForPhone(user, phoneNumber))
  );
};

export const PHONE_CHANGE_COOLDOWN_DAYS_VALUE = PHONE_CHANGE_COOLDOWN_DAYS;
